import type { ru } from '../ru';

export const accounting: typeof ru.accounting = {
  kicker: 'Admin',
  title: 'Accounting',
  tabsAria: 'Accounting sections',
  tabs: { summary: 'Summary', expenses: 'Expenses', incomes: 'Income', withdrawals: 'Withdrawals', settings: 'Settings' },
  common: {
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    deleting: 'Deleting…',
    loadFailed: 'Failed to load data',
    saved: 'Saved',
    deleted: 'Deleted',
    actions: 'Actions',
    dash: '—',
    from: 'From month',
    to: 'To month',
    reset: 'Current month',
    rangeInvalid: 'The period start is after its end.',
    rangeTooLong: 'The period is at most 36 months.',
    rangeAria: 'Period',
  },
  errors: {
    generic: 'Could not complete the action',
    sourceCodeExists: 'A source with this code already exists.',
    sourceCodeInvalid: 'Source code: a-z, 0-9, hyphen, underscore, up to 64 characters.',
    unknownSource: 'No such source.',
    unknownParticipant: 'No such participant.',
    dateInvalid: 'Invalid date.',
    amountInvalid: 'Invalid amount.',
    rateInvalid: 'Invalid rate: 0 to 100%.',
    shareInvalid: 'Invalid weight: 0 to 999999.9999.',
    monthInvalid: 'Invalid month.',
    periodInvalid: 'Invalid period: at most 36 months, start not after end.',
    receiptUrlInvalid: 'Invalid receipt link.',
    notFound: 'Record not found, refresh the page.',
    withdrawalNotFound: 'Withdrawal not found, refresh the page.',
    noteTooLong: 'Note: up to 500 characters.',
  },
  summary: {
    loadFailed: 'Failed to load the summary',
    empty: 'No data for the period yet.',
    sharesWarning: 'There are no active participants with a weight above zero. Payouts are not distributed, fix the weights in settings.',
    taxNote: (percent: string) => `Tax: ${percent} of revenue.`,
    monthsTitle: 'By month',
    monthsAria: 'Summary by month',
    columns: {
      month: 'Month',
      gross: 'Revenue',
      commission: 'Fees',
      tax: 'Tax',
      expenses: 'Expenses',
      net: 'Net',
      margin: 'Margin',
      payout: (name: string) => `Payout: ${name}`,
    },
    totalRow: 'Total',
    periodTitle: 'For a month',
    periodSelect: 'Period',
    allTime: 'All time',
    kpi: { gross: 'Revenue', commission: 'Fees', tax: 'Tax', expenses: 'Expenses', net: 'Net' },
    payoutsTitle: 'Participant payouts',
    payoutShare: 'Share of net',
    payoutReimbursement: 'Expense reimbursement',
    payoutTotal: 'Payout',
    payoutWithdrawn: 'Withdrawn',
    payoutBalance: 'Left to pay out',
    payoutOverpaid: 'Overpaid',
    shareOf: (percent: string) => `share ${percent}`,
    inactive: 'inactive',
    noParticipants: 'No active participants.',
  },
  withdrawals: {
    title: 'Withdrawals',
    add: 'Add withdrawal',
    loadFailed: 'Failed to load withdrawals',
    empty: 'No withdrawals for the period.',
    totalLabel: 'Total for the period',
    tableAria: 'Withdrawals list',
    columns: { date: 'Date', participant: 'Participant', amount: 'Amount', note: 'Note' },
    inactiveMark: 'inactive',
    editAria: (name: string, date: string) => `Edit withdrawal: ${name}, ${date}`,
    deleteAria: (name: string, date: string) => `Delete withdrawal: ${name}, ${date}`,
    dialog: {
      createTitle: 'New withdrawal',
      editTitle: 'Edit withdrawal',
      date: 'Date',
      participant: 'Participant',
      amount: 'Amount, ₽',
      note: 'Note',
      dateRequired: 'Enter a date.',
      participantRequired: 'Select a participant.',
      amountInvalid: 'Amount must be greater than zero, at most two decimal places.',
      noteTooLong: 'Note: up to 500 characters.',
    },
    deleteDialog: {
      title: 'Delete withdrawal?',
      body: (name: string, amount: string, date: string) => `The ${amount} withdrawal by ${name} on ${date} will be deleted permanently.`,
    },
  },
  expenses: {
    title: 'Expenses',
    add: 'Add expense',
    loadFailed: 'Failed to load expenses',
    empty: 'No expenses for the period.',
    totalLabel: 'Total for the period',
    tableAria: 'Expenses list',
    columns: { date: 'Date', category: 'Category', description: 'Description', amount: 'Amount', paidBy: 'Paid by', receipt: 'Receipt' },
    fromProject: 'Project funds',
    receiptOpen: 'Open',
    receiptAria: (category: string) => `Open receipt: ${category}`,
    editAria: (category: string, date: string) => `Edit expense: ${category}, ${date}`,
    deleteAria: (category: string, date: string) => `Delete expense: ${category}, ${date}`,
    dialog: {
      createTitle: 'New expense',
      editTitle: 'Edit expense',
      date: 'Date',
      category: 'Category',
      categoryHint: 'Pick from the list or type your own.',
      description: 'Description',
      amount: 'Amount, RUB',
      paidBy: 'Paid by',
      receipt: 'Receipt link',
      dateRequired: 'Enter a date.',
      categoryRequired: 'Enter a category.',
      categoryTooLong: 'Category: up to 64 characters.',
      descriptionTooLong: 'Description: up to 500 characters.',
      amountInvalid: 'The amount must be above zero, with at most two decimal places.',
      receiptTooLong: 'Link: up to 1000 characters.',
    },
    deleteDialog: {
      title: 'Delete expense?',
      body: (category: string, amount: string, date: string) => `The expense "${category}" of ${amount} from ${date} will be deleted permanently.`,
    },
  },
  incomes: {
    autoTitle: 'Store income',
    autoNote: 'Calculated automatically from paid orders, read-only.',
    autoEmpty: 'No paid store orders for the period.',
    autoAria: 'Store income by month',
    autoColumns: { month: 'Month', source: 'Source', orders: 'Orders', amount: 'Amount', commission: 'Fee' },
    manualTitle: 'Manual income',
    add: 'Add income',
    loadFailed: 'Failed to load income',
    manualEmpty: 'No manual income for the period.',
    manualAria: 'Manual income list',
    columns: { month: 'Month', source: 'Source', amount: 'Amount', note: 'Note' },
    editAria: (source: string, month: string) => `Edit income: ${source}, ${month}`,
    deleteAria: (source: string, month: string) => `Delete income: ${source}, ${month}`,
    dialog: {
      createTitle: 'New income',
      editTitle: 'Edit income',
      month: 'Month',
      source: 'Source',
      amount: 'Amount, RUB',
      note: 'Note',
      monthRequired: 'Enter a month.',
      sourceRequired: 'Choose a source.',
      amountInvalid: 'The amount must be above zero, with at most two decimal places.',
      noteTooLong: 'Note: up to 500 characters.',
    },
    deleteDialog: {
      title: 'Delete income?',
      body: (source: string, amount: string, month: string) => `The income "${source}" of ${amount} for ${month} will be deleted permanently.`,
    },
  },
  settings: {
    loadFailed: 'Failed to load settings',
    sources: {
      title: 'Income sources and fees',
      add: 'Add source',
      tableAria: 'Income sources',
      columns: { title: 'Name', code: 'Code', commission: 'Fee', note: 'Note' },
      storeBadge: 'Store',
      editAria: (title: string) => `Edit source: ${title}`,
      dialog: {
        createTitle: 'New source',
        editTitle: 'Edit source',
        code: 'Code',
        codeHint: 'Latin letters, digits, hyphen and underscore, up to 64 characters. Cannot be changed later.',
        title: 'Name',
        commission: 'Fee, %',
        note: 'Note',
        codeInvalid: 'Code: a-z, 0-9, hyphen, underscore, 1 to 64 characters.',
        titleRequired: 'Enter a name.',
        titleTooLong: 'Name: up to 128 characters.',
        percentInvalid: 'Percent must be a number from 0 to 100.',
        noteTooLong: 'Note: up to 500 characters.',
      },
    },
    participants: {
      title: 'Participants and weights',
      add: 'Add participant',
      tableAria: 'Participants',
      columns: { name: 'Name', share: 'Weight (parts)', percent: 'Share', active: 'Status' },
      active: 'Active',
      inactive: 'Inactive',
      editAria: (name: string) => `Edit participant: ${name}`,
      noActiveWeight: 'No active participants with a weight above zero, payouts are not distributed.',
      weightHint: 'A participant share = their weight / total weight of active participants.',
      deactivateHint: 'Inactive participants get no share, their expenses stay on the books.',
      dialog: {
        createTitle: 'New participant',
        editTitle: 'Edit participant',
        name: 'Name',
        share: 'Weight (parts)',
        shareHint: 'A plain number, e.g. 1, 1, 1 or 40, 30, 30. Fractions are fine.',
        active: 'Active',
        nameRequired: 'Enter a name.',
        nameTooLong: 'Name: up to 128 characters.',
        weightInvalid: 'Weight must be a number from 0 to 999999.9999, up to 4 decimal places.',
      },
    },
    tax: {
      title: 'Tax',
      label: 'Tax rate, % of revenue',
      hint: 'One current rate for all months.',
      invalid: 'Rate must be a number from 0 to 100.',
    },
  },
};
